import { Injectable } from '@angular/core';
import { SubmitPaymentInputs } from './pay-bind.service';
import {
  AccountHolder,
  AccountHolderCompany,
  AccountHolderPerson,
  DomesticAddress,
  EmailPreference,
  Payer,
  PaymentSetupAccount,
  PaymentTransaction,
  SaeAccountPayMethod,
  SaeRecurringPayment,
  SetupAccountExperienceRequest,
  TextingPreference,
} from '@core/models/api/request/setup-account-experience-request.model';
import {
  AddressOnAccount,
  NameOnAccount,
  PaymentMethod,
  PaymentType,
  PersonOnAccount,
  saeAccountPayMethodForPaymentPlanType,
  saePayPlanForPaymentPlanType,
} from '@core/models/views/pay-bind-page-model';
import { ProductModel } from '@entities/product/product.model';
import { MemberEntity } from '@entities/member/member.reducer';
import { DocumentDeliveryType, ProductType } from '@core/models/api/dsm-types';
import { AgencyModel } from '@entities/agency/agency.model';
import { ProducerUtils } from '@shared/utils/producer.utils';
import { Nullable } from '@shared/utils/type.utils';
import { PoliciesAndQuotes } from '@core/models/api/request/down-payment-experience-request.model';
import {
  DownPaymentExperienceResponse,
  EligiblePayplansAndPayMethods,
} from '@core/models/api/response/down-payment-experience-response.model';
import { MortgageEntity } from '@entities/mortgage/mortgage.reducer';
import { BasicAddressEntity } from '@entities/address/address.entity';
import { BankCardUtils } from '@shared/utils/bank-card.utils';

/**
 * Responsible for turning our store content into requests for Billing setupAccountExperience.
 * We do not talk to other services. All input is provided by the caller.
 * We're arguably part of PayBindService, extracted just for tidiness.
 */
@Injectable({
  providedIn: 'root',
})
export class SaeRequestService {
  constructor() {}

  composeSaeRequests(
    inputs: SubmitPaymentInputs
  ): SetupAccountExperienceRequest[] {
    if (!inputs.agencyModel.agent.agentState) {
      throw new Error(`Unknown producer state`);
    }
    const saeRequests: SetupAccountExperienceRequest[] = [];
    for (const paymentType of inputs.payBindPage.paymentTypes) {
      saeRequests.push(this.composeSaeRequest(inputs, paymentType));
    }
    return saeRequests;
  }

  private composeSaeRequest(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType
  ): SetupAccountExperienceRequest {
    const products = this.getProductsForPaymentType(inputs, paymentType);
    const pni = this.getPniForProduct(inputs, products[0].type);
    switch (paymentType.paymentPlan) {
      case 'ESCROW':
        return this.composeEscrowSaeRequest(inputs, paymentType, products, pni);
      case 'PAY IN FULL':
        return this.composeFullPaySaeRequest(
          inputs,
          paymentType,
          products,
          pni
        );
      default:
        return this.composeMonthlySaeRequest(
          inputs,
          paymentType,
          products,
          pni
        );
    }
  }

  private composeEscrowSaeRequest(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType,
    products: ProductModel[],
    pni: MemberEntity
  ): SetupAccountExperienceRequest {
    const mortgage = this.findEscrowMortgage(
      inputs.mortgages,
      paymentType,
      products
    );
    const request: SetupAccountExperienceRequest = {
      body: {
        dayofMonthDue: '01',
        accountPayMethod: 'Third Party',
        payPlan: saePayPlanForPaymentPlanType(paymentType.paymentPlan),
        producerNumber: this.getProducerNumber(inputs.agencyModel),
        producerState: this.getProducerState(inputs.agencyModel),
        distributionChannel: this.getDistributionChannel(inputs),
        thirdPartyType: 'ESCROW',
        accountHolder: this.buildEscrowAccountHolder(inputs, pni, mortgage),
        emailPreference: this.buildEmailPreference(
          pni.emailAddress,
          products[0].docDelPreference
        ),
        textingPreference: this.buildTextingPreference(pni.homeNumber),
        payment: this.composeEscrowPayment(),
        policiesAndQuotes: this.buildPoliciesAndQuotes(products),
      },
    };
    return request;
  }

  private composeFullPaySaeRequest(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType,
    products: ProductModel[],
    pni: MemberEntity
  ): SetupAccountExperienceRequest {
    const downMethod = this.assertDownPaymentMethod(paymentType);
    const payplan = this.selectDownPaymentPlan(
      inputs,
      paymentType,
      downMethod,
      true
    );
    const downPayment = this.buildDownPayment(
      inputs,
      paymentType,
      downMethod,
      pni,
      payplan
    );
    return {
      body: {
        dayofMonthDue: downMethod.dayOfMonthDue || '01',
        accountPayMethod: this.saeAccountPayMethodFromDownPaymentPayMethod(
          payplan.payMethod
        ),
        payPlan: 'FULL PAYMENT PLAN',
        producerNumber: this.getProducerNumber(inputs.agencyModel),
        producerState: this.getProducerState(inputs.agencyModel),
        distributionChannel: this.getDistributionChannel(inputs),
        accountHolder: this.buildAccountHolder(downMethod.accountHolder, pni),
        emailPreference: this.buildEmailPreference(
          pni.emailAddress,
          products[0].docDelPreference
        ),
        textingPreference: this.buildTextingPreference(pni.homeNumber),
        payment: downPayment,
        policiesAndQuotes: this.buildPoliciesAndQuotes(products),
      },
    };
  }

  private saeAccountPayMethodFromDownPaymentPayMethod(
    input: string
  ): SaeAccountPayMethod {
    switch ((input || '').toUpperCase()) {
      case 'DIRECT BILL':
        return 'Direct Bill';
      case 'MONTHLY RECURRING BANKCARD':
        return 'Recurring BankCard';
      case 'MONTHLY EFT':
        return 'Recurring EFT';
    }
    return 'Direct Bill';
  }

  private composeMonthlySaeRequest(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType,
    products: ProductModel[],
    pni: MemberEntity
  ): SetupAccountExperienceRequest {
    let downMethod: PaymentMethod;
    let recurMethod: PaymentMethod | undefined;

    // Monthly Direct Bill allows down payment alone, so recurring is optional in that case
    if (
      paymentType.paymentPlan === 'MONTHLY DIRECT' &&
      !paymentType.paymentMethods?.some(
        (pm) =>
          pm.occurrence === 'Both' || pm.occurrence === 'Recurring Payment'
      )
    ) {
      downMethod = this.assertDownPaymentMethod(paymentType);
    } else {
      ({ downMethod, recurMethod } = this.assertPaymentMethods(paymentType));
    }

    const payplan = this.selectDownPaymentPlan(
      inputs,
      paymentType,
      recurMethod || downMethod
    );
    const downPayment = this.buildDownPayment(
      inputs,
      paymentType,
      downMethod,
      pni,
      payplan
    );
    const recurringPayment = recurMethod
      ? this.buildRecurringPayment(
          payplan,
          recurMethod,
          paymentType,
          inputs,
          pni
        )
      : undefined;
    return {
      body: {
        dayofMonthDue: recurMethod?.dayOfMonthDue || '01',
        accountPayMethod: saeAccountPayMethodForPaymentPlanType(
          paymentType.paymentPlan
        ),
        payPlan: saePayPlanForPaymentPlanType(paymentType.paymentPlan),
        producerNumber: this.getProducerNumber(inputs.agencyModel),
        producerState: this.getProducerState(inputs.agencyModel),
        distributionChannel: this.getDistributionChannel(inputs),
        accountHolder: this.buildAccountHolder(downMethod.accountHolder, pni),
        emailPreference: this.buildEmailPreference(
          pni.emailAddress,
          products[0].docDelPreference
        ),
        textingPreference: this.buildTextingPreference(pni.homeNumber),
        payment: downPayment,
        recurringPayment,
        policiesAndQuotes: this.buildPoliciesAndQuotes(products),
      },
    };
  }

  private buildRecurringPayment(
    payplan: EligiblePayplansAndPayMethods,
    paymentMethod: PaymentMethod,
    paymentType: PaymentType,
    inputs: SubmitPaymentInputs,
    pni: MemberEntity
  ): SaeRecurringPayment | undefined {
    switch (paymentType.paymentPlan) {
      case 'ESCROW':
      case 'PAY IN FULL':
        return undefined;
    }
    switch (paymentMethod.paymentMethod) {
      case 'Bank Account':
        return {
          electronicFunds: {
            bankName: paymentMethod.bankAccount!.bankName || '',
            bankRoutingNumber: paymentMethod.bankAccount!.routingNumber || '',
            bankAccountNumber:
              paymentMethod.bankAccount!.bankAccountNumber || '',
            bankAccountType: 'CHECKING',
          },
          payer: this.buildPayer(paymentType, paymentMethod, inputs, pni),
        };
      case 'Credit Debit Card':
        return {
          // SaeRecurringPayment does not have a bankCardWithClearPan member.
          // We're making one up, to pass content thru to Paymentech. Hence the cast to any.
          bankCardWithClearPan: {
            cardNumber: paymentMethod.bankCard!.cardNumber,
            cardType:
              BankCardUtils.mapCardToCardType(
                paymentMethod.bankCard!.cardType
              ) || '',
            expirationDate: paymentMethod.bankCard!.expirationDate,
            cardVerificationValue: paymentMethod.bankCard!.cvv,
          },
          payer: this.buildPayer(paymentType, paymentMethod, inputs, pni),
        } as any;
    }
    return undefined;
  }

  private buildDownPayment(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    pni: MemberEntity,
    payplan: EligiblePayplansAndPayMethods
  ): PaymentSetupAccount {
    let amount = '';
    if (paymentType.paymentPlan === 'PAY IN FULL') {
      amount = this.sanitizeAmount(payplan.fullPayAmount);
    } else {
      amount = this.sanitizeAmount(payplan.totalDownPaymentWithFees);
    }
    return {
      businessChannel: 'INTERNET-ELECTRONIC BILL',
      amount,
      paymentTransaction: this.buildPaymentTransaction(paymentMethod),
      payer: this.buildPayer(paymentType, paymentMethod, inputs, pni),
    };
  }

  private sanitizeAmount(amount?: string): string {
    if (!amount) return '';
    return amount.trim().replace(/\,/g, '');
  }

  private buildPayer(
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    inputs: SubmitPaymentInputs,
    pni: MemberEntity
  ): Payer {
    const name = paymentMethod.accountHolder?.name;
    if (!name) throw new Error(`Account holder missing`);
    const address = this.getAddress(paymentType, paymentMethod, inputs);
    return {
      firstName: name.firstName || '',
      middleName: name.middleName || '',
      lastName: name.lastName || '',
      displayName: this.buildDisplayName(name),
      addressLine1: address.addressLine1 || '',
      addressLine2: address.addressLine2 || '',
      city: address.city || '',
      state: address.state || '',
      postalCode: (address.zipCode || (address as any).postalCode || '').split(
        '-'
      )[0],
      phone: pni.homeNumber || '',
      emailAddress: pni.emailAddress || '',
    };
  }

  private buildDisplayName(name: NameOnAccount): string {
    return `${name.firstName} ${name.lastName}`;
  }

  private getAddress(
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    inputs: SubmitPaymentInputs
  ): AddressOnAccount {
    if (this.addressNullIfEmpty(paymentMethod.accountHolder?.address)) {
      return paymentMethod.accountHolder!.address!;
    }
    const pni = this.getPniForProduct(
      inputs,
      paymentType.products[0]?.productType
    );
    if (pni.address) {
      return {
        addressLine1: pni.address.addressLine1,
        addressLine2: pni.address.addressLine2,
        city: pni.address.city,
        state: pni.address.state,
        zipCode: pni.address.postalCode?.split('-')[0],
      };
    }
    throw new Error(`Unable to determine billing address`);
  }

  private buildPaymentTransaction(
    paymentMethod: PaymentMethod
  ): PaymentTransaction {
    const transaction: PaymentTransaction = {
      paymentMethod: '',
    };
    switch (paymentMethod.paymentMethod) {
      case 'Bank Account':
        transaction.paymentMethod = 'electronicFunds';
        transaction.electronicFunds = {
          bankName: paymentMethod.bankAccount!.bankName || '',
          bankRoutingNumber: paymentMethod.bankAccount!.routingNumber || '',
          bankAccountNumber: paymentMethod.bankAccount!.bankAccountNumber || '',
          bankAccountType: 'CHECKING',
        };
        break;
      case 'Credit Debit Card':
        // This will get replaced before going out to SAE, when we call Paymentech.
        transaction.paymentMethod = 'bankCardWithClearPan';
        transaction.bankCardWithClearPan = {
          cardNumber: paymentMethod.bankCard!.cardNumber,
          cardType:
            BankCardUtils.mapCardToCardType(paymentMethod.bankCard!.cardType) ||
            '',
          expirationDate: paymentMethod.bankCard!.expirationDate,
          cardVerificationValue: paymentMethod.bankCard!.cvv,
        };
        break;
      case 'Agency Sweep':
        // Not listed in SAE's OAS:
        transaction.paymentMethod = 'EZSWEEP';
        transaction.ezSweep = {
          ezSweepCheckNumber:
            paymentMethod.agencySweep!.agencySweepPaymentType === 'cash'
              ? 'cash'
              : paymentMethod.agencySweep!.checkNumber!.toString(),
        };
        break;
      default:
        throw new Error(
          `Unexpected payment method ${JSON.stringify(
            paymentMethod.paymentMethod
          )}`
        );
    }
    return transaction;
  }

  private selectDownPaymentPlan(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    fullpay = false
  ): EligiblePayplansAndPayMethods {
    let payplan: EligiblePayplansAndPayMethods | undefined;
    const policyNumberByQuoteId: { [quoteId: string]: string } = {};
    for (const product of inputs.products) {
      policyNumberByQuoteId[product.quoteId || ''] = product.policyNumber || '';
    }
    for (const dpResponse of inputs.downPayments) {
      if (
        dpResponse.policyLevel.find((pl) =>
          paymentType.products.find(
            (p) =>
              p.quoteId === pl.displayPolicyNumber ||
              policyNumberByQuoteId[p.quoteId] === pl.displayPolicyNumber
          )
        )
      ) {
        switch (paymentMethod.paymentMethod) {
          case 'Bank Account':
            payplan = dpResponse.eligiblePayplansAndPayMethods.find(
              (epp) => epp.payMethod === 'MONTHLY EFT'
            );
            break;
          case 'Credit Debit Card':
            payplan = dpResponse.eligiblePayplansAndPayMethods.find(
              (epp) => epp.payMethod === 'MONTHLY RECURRING BANKCARD'
            );
            break;
          case 'Agency Sweep':
            payplan = dpResponse.eligiblePayplansAndPayMethods.find(
              (epp) => epp.payMethod === 'MONTHLY DIRECT'
            );
            break;
          case 'Escrow':
            payplan = dpResponse.eligiblePayplansAndPayMethods.find(
              (epp) => epp.payMethod === 'MONTHLY DIRECT'
            );
            break;
        }
        if (!payplan && fullpay) {
          payplan = dpResponse.eligiblePayplansAndPayMethods.find(
            (epp) => epp.payPlan === 'FULL PAYMENT PLAN'
          );
        }
        if (payplan) {
          return payplan;
        }
      }
    }
    throw new Error(`Pay plan not found in downPayments response`);
  }

  /* Locates PaymentMethod for down payment and recurring payment.
   * Exception if one doesn't exist, or if the account holders don't match.
   * They may be the same thing.
   */
  private assertPaymentMethods(paymentType: PaymentType): {
    downMethod: PaymentMethod;
    recurMethod: PaymentMethod;
  } {
    if (paymentType.paymentPlan === 'ESCROW') {
      const method: PaymentMethod = {
        paymentMethod: 'Escrow',
        occurrence: 'Both',
      };
      return {
        downMethod: method,
        recurMethod: method,
      };
    }
    const downMethod = paymentType.paymentMethods?.find(
      (pm) => pm.occurrence === 'Both' || pm.occurrence === 'Down Payment'
    );
    const recurMethod = paymentType.paymentMethods?.find(
      (pm) => pm.occurrence === 'Both' || pm.occurrence === 'Recurring Payment'
    );
    if (!downMethod || !recurMethod) {
      throw new Error(`Payment type missing method`);
    }
    if (
      !this.accountHolderEquivalent(
        downMethod.accountHolder,
        recurMethod.accountHolder
      )
    ) {
      throw new Error(
        `Payment type has multiple methods with mismatched account holder`
      );
    }
    return { downMethod, recurMethod };
  }

  /* Locates PaymentMethod for down payment only.
   */
  private assertDownPaymentMethod(paymentType: PaymentType): PaymentMethod {
    if (paymentType.paymentPlan === 'ESCROW') {
      const method: PaymentMethod = {
        paymentMethod: 'Escrow',
        occurrence: 'Both',
      };
      return method;
    }
    const downMethod = paymentType.paymentMethods?.find(
      (pm) => pm.occurrence === 'Both' || pm.occurrence === 'Down Payment'
    );
    if (!downMethod) {
      throw new Error(`Payment type missing method`);
    }
    return downMethod;
  }

  private accountHolderEquivalent(
    a?: PersonOnAccount,
    b?: PersonOnAccount
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.name !== b.name) {
      if (!a.name || !b.name) return false;
      if (a.name.firstName !== b.name.firstName) return false;
      if (a.name.lastName !== b.name.lastName) return false;
      // Don't assert middleName or suffix.
    }
    const aAddress = this.addressNullIfEmpty(a.address);
    const bAddress = this.addressNullIfEmpty(b.address);
    if (aAddress !== bAddress) {
      if (!aAddress || !bAddress) return false;
      if (aAddress.addressLine1 !== bAddress.addressLine1) return false;
      if (aAddress.city !== bAddress.city) return false;
      if (aAddress.state !== bAddress.state) return false;
      if ((aAddress as any).zipCode !== (bAddress as any).zipCode) return false;
      // Don't assert addressLine2.
    }
    return true;
  }

  private addressNullIfEmpty(
    address?: BasicAddressEntity
  ): BasicAddressEntity | null {
    if (!address) return null;
    if (!address.addressLine1) return null;
    return address;
  }

  private findEscrowMortgage(
    mortgages: MortgageEntity[],
    paymentType: PaymentType,
    products: ProductModel[]
  ): MortgageEntity {
    // TODO How to know which mortgage was selected?
    for (const mortgage of mortgages) {
      if (
        mortgage.type === 'Mortgagee1' &&
        mortgage.mortgageType === 'Mortgagee_Ext'
      ) {
        return mortgage;
      }
    }
    throw new Error(`Mortgage not found for escrow payment.`);
  }

  private composeEscrowPayment(): PaymentSetupAccount {
    return {
      businessChannel: 'INTERNET-ELECTRONIC BILL',
      amount: '0.00',
      paymentTransaction: {
        // Ignored but must be present.
        paymentMethod: 'Bankcard',
      },
    };
  }

  private allProductsPresentInDownPaymentResponse(
    products: ProductModel[],
    dpResponse: DownPaymentExperienceResponse
  ): boolean {
    if (products.length < 1) return false;
    for (const product of products) {
      const policyLevel = dpResponse.policyLevel.find((pl) => {
        if (pl.displayPolicyNumber === product.quoteId) return true;
        if (pl.displayPolicyNumber === product.policyNumber) return true;
        return false;
      });
      if (!policyLevel) return false;
    }
    return true;
  }

  /** Throws exception if no products found; returned length is at least one.
   */
  private getProductsForPaymentType(
    inputs: SubmitPaymentInputs,
    paymentType: PaymentType
  ): ProductModel[] {
    const products: ProductModel[] = [];
    for (const product of inputs.payBindPage.products) {
      if (paymentType.products.find((p) => p.productType === product.type)) {
        const realProduct = inputs.products.find(
          (rp) => rp.type === product.type
        );
        if (!realProduct) throw new Error(`Product ${product.type} not found`);
        products.push(realProduct);
      }
    }
    if (!products.length) {
      throw new Error(`No products found for paymentType`);
    }
    return products;
  }

  /** Throws exception if none found.
   */
  private getPniForProduct(
    inputs: SubmitPaymentInputs,
    productType: ProductType
  ): MemberEntity {
    for (const member of inputs.members) {
      for (const role of member.policyRoles) {
        if (role.entityType !== 'policyHolder') continue;
        if (role.roleSequence !== 'primary') continue;
        return member;
      }
    }
    throw new Error(`PNI not found for product ${JSON.stringify(productType)}`);
  }

  private getProducerNumber(agencyModel: AgencyModel): string {
    return ProducerUtils.extractProducerNumberForBilling(agencyModel);
  }

  private getProducerState(agencyModel: AgencyModel): string {
    if (!agencyModel.agent.agentState) {
      throw new Error(`Agent state unset`);
    }
    return agencyModel.agent.agentState;
  }

  private getDistributionChannel(inputs: SubmitPaymentInputs): string {
    return inputs.isNss ? 'NSS' : 'INDEPENDENT AGENT';
  }

  private buildEscrowAccountHolder(
    inputs: SubmitPaymentInputs,
    pni: MemberEntity,
    mortgage: MortgageEntity
  ): AccountHolder {
    let address: DomesticAddress = {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      addressCleansingBypassReason: 'Use customer preferred city',
    };
    if (pni.address) {
      address.addressLine1 = pni.address.addressLine1 || '';
      address.addressLine2 = pni.address.addressLine2 || '';
      address.city = pni.address.city || '';
      address.state = pni.address.state || '';
      address.zip = (pni.address.postalCode || '').split('-')[0];
    }
    let accountHolderPerson: AccountHolderPerson = {
      firstName: '',
      lastName: '',
    };
    if (pni.person) {
      accountHolderPerson.firstName = pni.person.firstName || '';
      accountHolderPerson.middleName = pni.person.middleName || '';
      accountHolderPerson.lastName = pni.person.lastName || '';
      accountHolderPerson.suffix = this.suffixForBilling(pni.person.suffix || '');
    }
    return {
      name: {
        person: accountHolderPerson,
      },
      address: {
        optOutFromNCOAAddressUpdate: 'Y',
        domesticAddress: address,
      },
    };
  }

  private buildAccountHolder(
    person: PersonOnAccount | null | undefined,
    pni: MemberEntity | null
  ): AccountHolder {
    let address: DomesticAddress = {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      addressCleansingBypassReason: 'Use customer preferred city',
    };
    if (this.addressNullIfEmpty(person?.address)) {
      address.addressLine1 = person!.address!.addressLine1 || '';
      address.addressLine2 = person!.address!.addressLine2 || '';
      address.city = person!.address!.city || '';
      address.state = person!.address!.state || '';
      address.zip = (person!.address!.postalCode || '').split('-')[0];
    } else if (pni?.address) {
      address.addressLine1 = pni.address.addressLine1 || '';
      address.addressLine2 = pni.address.addressLine2 || '';
      address.city = pni.address.city || '';
      address.state = pni.address.state || '';
      address.zip = (pni.address.postalCode || '').split('-')[0];
    }
    let accountHolderPerson: AccountHolderPerson = {
      firstName: '',
      //middleName?: string;
      lastName: '',
      //suffix?: string;
      //addresseeLine2?: string;
      //addresseeLine3?: string;
    };
    if (person?.name) {
      accountHolderPerson.firstName = person.name.firstName || '';
      accountHolderPerson.middleName = person.name.middleName;
      accountHolderPerson.lastName = person.name.lastName || '';
      accountHolderPerson.suffix = this.suffixForBilling(person.name.suffix);
    } else if (pni?.person) {
      accountHolderPerson.firstName = pni.person.firstName || '';
      accountHolderPerson.middleName = pni.person.middleName || '';
      accountHolderPerson.lastName = pni.person.lastName || '';
      accountHolderPerson.suffix = this.suffixForBilling(pni.person.suffix || '');
    }
    return {
      name: {
        person: accountHolderPerson,
      },
      address: {
        optOutFromNCOAAddressUpdate: 'Y',
        domesticAddress: address,
      },
    };
  }

  private suffixForBilling(input: string | undefined): string | undefined {
    switch (input) {
      // 'jr' and 'sr' are ok.
      case 'c_Ir': return 'I';
      case 'c_II': return 'II';
      case 'c_III': return 'III';
      case 'c_IV': return 'IV';
      case 'c_V_Ext': return 'V';
      case 'c_VI_Ext': return 'VI';
    }
    return input;
  }

  private buildEmailPreference(
    emailAddress: Nullable<string>,
    docDeliveryPreference: Nullable<DocumentDeliveryType>
  ): EmailPreference {
    const emailPreference = {} as EmailPreference;
    const isPaperless = docDeliveryPreference === 'OnlineAccountAccess';
    emailPreference.consentStatus = 'Y';
    emailPreference.paperlessPreference = isPaperless ? 'EMAL' : 'UMAL';
    emailPreference.emailAddress = emailAddress || '';
    emailPreference.paymentReminderPreference = isPaperless ? 'Y' : 'N';
    emailPreference.paymentConfirmationPreference = isPaperless ? 'Y' : 'N';
    return emailPreference;
  }

  private buildTextingPreference(
    mobilePhoneNumber: Nullable<string>
  ): TextingPreference[] {
    const textingPreferences = [];
    const textingPref = {} as TextingPreference;
    textingPref.preferenceType = 'Payment Reminder Text';
    textingPref.preferenceValue = 'No';
    textingPref.deliveryMethod = 'BillingTextDelivery';
    textingPref.deliveryInformation = mobilePhoneNumber || '';
    textingPreferences.push(textingPref);
    textingPreferences.push({
      ...textingPref,
      preferenceType: 'Payment Notification Text',
    });
    return textingPreferences;
  }

  private buildPoliciesAndQuotes(
    products: ProductModel[]
  ): PoliciesAndQuotes[] {
    return products.map((product) => {
      const pq: PoliciesAndQuotes = {
        quoteId: product.quoteId || '',
      };
      if (product.policyNumber) {
        pq.policyNumber = product.policyNumber;
      }
      return pq;
    });
  }
}
